import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  AlertCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  CalendarIcon,
  Loader2Icon,
  ExternalLinkIcon,
  DownloadIcon,
  PrinterIcon,
  RefreshCwIcon,
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { DivisionFilter, useDivision } from '../contexts/DivisionContext'
import { usePermissions } from '../hooks/usePermissions'
import {
  MobilePage,
  MobilePageHeader,
  MobileListRow,
  MobileEmptyState,
  MobileStatStrip,
  MobileRowAction,
  mobile,
} from '../components/mobile'
import { EndOfDayDatePrompt } from '../components/endOfDay/EndOfDayDatePrompt'
import { EndOfDayJobPanel } from '../components/endOfDay/EndOfDayJobPanel'
import {
  fetchEndOfDayCore,
  fetchEndOfDayWidgets,
  friendlyEndOfDayError,
  jobDetailPath,
  type EndOfDayCoreSnapshot,
  type EndOfDayJob,
  type EndOfDaySnapshot,
  type EndOfDayWidgets,
} from '../api/endOfDay'
import { recordEodSignoff } from '../api/eodSignoffs'
import { getStatusColor, formatStatusLabel } from '../lib/jobStatusWorkflow'
import { downloadEodCsv, printEodSummary } from '../lib/eodExport'
import { getEodWeeklyRollupStatus } from '../lib/eodWeeklyRollup'

const EOD_DATE_KEY = 'rbc-eod-closeout-date'

function todayYmd(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function fmtMoney(n: number): string {
  return `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function JobBadges({ job }: { job: EndOfDayJob }) {
  return (
    <>
      <span className={`${mobile.badge} ${getStatusColor(job.status)}`}>
        {formatStatusLabel(job.status)}
      </span>
      {job.isException && (
        <span className={`${mobile.badge} bg-amber-100 text-amber-800`}>Exception</span>
      )}
      {job.hasOpenPunch && (
        <span className={`${mobile.badge} bg-red-100 text-red-800`}>Open punch</span>
      )}
      {job.checklistTotal > 0 && job.checklistDone < job.checklistTotal && (
        <span className={`${mobile.badge} bg-orange-100 text-orange-800`}>
          Checklist {Math.round((job.checklistDone / job.checklistTotal) * 100)}%
        </span>
      )}
    </>
  )
}

interface SectionProps {
  title: string
  jobs: EndOfDayJob[]
  empty: string
  canSeePricing: boolean
  canAct: boolean
  onOpen: (job: EndOfDayJob) => void
  onNavigate: (job: EndOfDayJob) => void
}

function EndOfDayJobRow({
  job,
  canSeePricing,
  canAct,
  onOpen,
  onNavigate,
}: Omit<SectionProps, 'title' | 'jobs' | 'empty'> & { job: EndOfDayJob }) {
  return (
    <MobileListRow
      title={job.title}
      subtitle={job.job_number ? `#${job.job_number}` : job.customer?.company_name}
      badges={<JobBadges job={job} />}
      meta={
        <>
          <div className="truncate">{job.customer?.company_name || '—'}</div>
          <div className="truncate">{job.property?.address || '—'}</div>
          {job.techs.length > 0 && <div>Crew: {job.techs.join(', ')}</div>}
          {job.checklistTotal > 0 && (
            <div>
              Checklist {job.checklistDone}/{job.checklistTotal} · Photos {job.photos}
            </div>
          )}
          {canSeePricing && (
            <div>
              Q {fmtMoney(job.daily_quoted_revenue)} · A {fmtMoney(job.daily_actual_revenue)}
            </div>
          )}
        </>
      }
      onClick={() => onNavigate(job)}
      footer={
        canAct ? (
          <>
            {job.customer?.phone && (
              <MobileRowAction
                onClick={() => window.open(`tel:${job.customer?.phone}`, '_self')}
              >
                Call
              </MobileRowAction>
            )}
            <MobileRowAction onClick={() => onOpen(job)}>Quick view</MobileRowAction>
          </>
        ) : undefined
      }
    />
  )
}

function EndOfDaySection({
  title,
  jobs,
  empty,
  canSeePricing,
  canAct,
  onOpen,
  onNavigate,
}: SectionProps) {
  return (
    <>
      <p className={mobile.sectionLabel}>{title} ({jobs.length})</p>

      {/* Mobile / PWA list */}
      <div className="native-mobile-list space-y-2 mb-6">
        {jobs.length === 0 ? (
          <MobileEmptyState title={empty} />
        ) : (
          jobs.map((job) => (
            <EndOfDayJobRow
              key={job.id}
              job={job}
              canSeePricing={canSeePricing}
              canAct={canAct}
              onOpen={onOpen}
              onNavigate={onNavigate}
            />
          ))
        )}
      </div>

      {/* Desktop table — native-mobile-list is hidden at lg+ without this counterpart */}
      <div className="native-desktop-table mb-6 bg-white rounded-lg border border-gray-200 overflow-hidden">
        {jobs.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">{empty}</div>
        ) : (
          <table className="w-full table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Job
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Property
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Crew
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                {canSeePricing && (
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Revenue
                  </th>
                )}
                {canAct && (
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-36">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {jobs.map((job) => (
                <tr
                  key={job.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => onNavigate(job)}
                >
                  <td className="px-4 py-3 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{job.title}</div>
                    {job.job_number && (
                      <div className="text-xs text-gray-500 truncate">#{job.job_number}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 min-w-0 text-sm text-gray-900 truncate">
                    {job.customer?.company_name || '—'}
                  </td>
                  <td className="px-4 py-3 min-w-0 text-sm text-gray-600 truncate">
                    {job.property?.address || '—'}
                  </td>
                  <td className="px-4 py-3 min-w-0 text-sm text-gray-600 truncate">
                    {job.techs.length > 0 ? job.techs.join(', ') : '—'}
                  </td>
                  <td className="px-4 py-3 min-w-0">
                    <div className="flex flex-wrap gap-1">
                      <JobBadges job={job} />
                    </div>
                    {job.checklistTotal > 0 && (
                      <div className="mt-1 text-xs text-gray-500">
                        Checklist {job.checklistDone}/{job.checklistTotal} · Photos {job.photos}
                      </div>
                    )}
                  </td>
                  {canSeePricing && (
                    <td className="px-4 py-3 min-w-0 text-sm text-gray-600 whitespace-nowrap">
                      Q {fmtMoney(job.daily_quoted_revenue)}
                      <br />
                      A {fmtMoney(job.daily_actual_revenue)}
                    </td>
                  )}
                  {canAct && (
                    <td className="px-4 py-3 min-w-0" onClick={(e) => e.stopPropagation()}>
                      <div className="flex flex-wrap gap-2">
                        {job.customer?.phone && (
                          <button
                            type="button"
                            onClick={() => window.open(`tel:${job.customer?.phone}`, '_self')}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            Call
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => onOpen(job)}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          Quick view
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}

function KpiSkeleton() {
  return (
    <div className="animate-pulse bg-gray-100 rounded-lg h-20 border border-gray-200" />
  )
}

function WidgetCount({ loading, value }: { loading: boolean; value: number }) {
  if (loading) {
    return <span className="inline-block h-8 w-10 bg-gray-100 rounded animate-pulse" />
  }
  return <>{value}</>
}

export function EndOfDay() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { currentDivision } = useDivision()
  const { can, canSeePricing, isAdmin, loading: permLoading } = usePermissions()

  const [closeoutDate, setCloseoutDate] = useState(() => localStorage.getItem(EOD_DATE_KEY) || todayYmd())
  const [showDatePrompt, setShowDatePrompt] = useState(true)
  const [coreSnapshot, setCoreSnapshot] = useState<EndOfDayCoreSnapshot | null>(null)
  const [widgets, setWidgets] = useState<EndOfDayWidgets | null>(null)
  const [coreLoading, setCoreLoading] = useState(true)
  const [widgetsLoading, setWidgetsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [panelJob, setPanelJob] = useState<EndOfDayJob | null>(null)
  const [crewFilter, setCrewFilter] = useState('')
  const [serviceFilter, setServiceFilter] = useState('')
  const [signingOff, setSigningOff] = useState(false)

  const canAct = can('jobs.dispatch') || can('review.approve') || isAdmin

  const snapshot = useMemo((): EndOfDaySnapshot | null => {
    if (!coreSnapshot) return null
    return {
      ...coreSnapshot,
      unassignedInboxCount: widgets?.unassignedInboxCount ?? 0,
      unapprovedTimeCount: widgets?.unapprovedTimeCount ?? 0,
      signoffs: widgets?.signoffs ?? [],
    }
  }, [coreSnapshot, widgets])

  const loading = coreLoading

  const load = useCallback(async () => {
    setCoreLoading(true)
    setWidgetsLoading(true)
    setError(null)
    try {
      const core = await fetchEndOfDayCore(closeoutDate, currentDivision)
      setCoreSnapshot(core)
      setCoreLoading(false)

      fetchEndOfDayWidgets(closeoutDate, currentDivision, user?.id)
        .then(setWidgets)
        .catch(() =>
          setWidgets({ unassignedInboxCount: 0, unapprovedTimeCount: 0, signoffs: [] })
        )
        .finally(() => setWidgetsLoading(false))
    } catch (err) {
      setError(friendlyEndOfDayError(err))
      setCoreLoading(false)
      setWidgetsLoading(false)
    }
  }, [closeoutDate, currentDivision, user?.id])

  useEffect(() => {
    if (permLoading) return
    load()
  }, [load, permLoading])

  const filtered = useMemo(() => {
    if (!snapshot) return null
    const filterJob = (job: EndOfDayJob) => {
      if (serviceFilter && job.service_type !== serviceFilter) return false
      if (crewFilter && !job.techs.some((t) => t.toLowerCase().includes(crewFilter.toLowerCase()))) {
        return false
      }
      return true
    }
    return {
      ...snapshot,
      exceptions: snapshot.exceptions.filter(filterJob),
      inProgressToday: snapshot.inProgressToday.filter(filterJob),
      otherInProgress: snapshot.otherInProgress.filter(filterJob),
      notStarted: snapshot.notStarted.filter(filterJob),
      completedToday: snapshot.completedToday.filter(filterJob),
    }
  }, [snapshot, crewFilter, serviceFilter])

  const serviceTypes = useMemo(() => {
    if (!snapshot) return []
    const set = new Set<string>()
    const all = [
      ...snapshot.exceptions,
      ...snapshot.inProgressToday,
      ...snapshot.otherInProgress,
      ...snapshot.notStarted,
      ...snapshot.completedToday,
    ]
    all.forEach((j) => {
      if (j.service_type) set.add(j.service_type)
    })
    return Array.from(set).sort()
  }, [snapshot])

  const handleDateConfirm = (date: string) => {
    setCloseoutDate(date)
    localStorage.setItem(EOD_DATE_KEY, date)
    setShowDatePrompt(false)
  }

  const handleSignoff = async (phase: 'office' | 'final') => {
    if (!filtered || !canAct) return
    setSigningOff(true)
    try {
      await recordEodSignoff({
        date: closeoutDate,
        divisionId: currentDivision?.id,
        phase,
        exceptionCount: filtered.exceptions.length,
        scheduledCount: filtered.scheduledCount,
        completedCount: filtered.completedToday.length,
      })
      await load()
    } catch (err) {
      alert(friendlyEndOfDayError(err))
    } finally {
      setSigningOff(false)
    }
  }

  const officeSigned = filtered?.signoffs.some((s) => s.phase === 'office')
  const finalSigned = filtered?.signoffs.some((s) => s.phase === 'final')
  const weeklyStatus = getEodWeeklyRollupStatus()

  if (coreLoading && !coreSnapshot) {
    return (
      <MobilePage>
        <MobilePageHeader
          title="End of Day Close-Out"
          subtitle="Loading…"
        />
        <div className="lg:hidden mb-4 grid grid-cols-2 gap-2">
          <KpiSkeleton />
          <KpiSkeleton />
          <KpiSkeleton />
          <KpiSkeleton />
        </div>
        <div className="hidden lg:grid grid-cols-4 gap-4 mb-6">
          <KpiSkeleton />
          <KpiSkeleton />
          <KpiSkeleton />
          <KpiSkeleton />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <KpiSkeleton />
          <KpiSkeleton />
        </div>
        <div className="flex flex-col items-center justify-center min-h-[200px] gap-3">
          <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
          <p className="text-sm text-gray-500">Loading end of day snapshot…</p>
        </div>
      </MobilePage>
    )
  }

  return (
    <MobilePage>
      <EndOfDayDatePrompt
        isOpen={showDatePrompt}
        initialDate={closeoutDate}
        onConfirm={handleDateConfirm}
      />

      <MobilePageHeader
        title="End of Day Close-Out"
        subtitle={new Date(closeoutDate + 'T12:00:00').toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="date"
              value={closeoutDate}
              onChange={(e) => {
                setCloseoutDate(e.target.value)
                localStorage.setItem(EOD_DATE_KEY, e.target.value)
              }}
              className="text-sm border border-gray-300 rounded-lg px-2 py-1"
            />
            <DivisionFilter />
            <button
              type="button"
              onClick={() => load()}
              disabled={loading}
              className="inline-flex items-center gap-1 px-2 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              aria-label="Refresh end of day data"
            >
              <RefreshCwIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap gap-2">
        <Link
          to={`/dispatch/${closeoutDate}`}
          className="inline-flex items-center gap-1 px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <ExternalLinkIcon className="w-4 h-4" />
          Open dispatch board
        </Link>
        {filtered && (
          <>
            <button
              type="button"
              onClick={() => downloadEodCsv(filtered)}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <DownloadIcon className="w-4 h-4" />
              CSV
            </button>
            <button
              type="button"
              onClick={() => printEodSummary(filtered)}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              <PrinterIcon className="w-4 h-4" />
              PDF / Print
            </button>
          </>
        )}
      </div>

      {error && (
        <div className="p-4 mb-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <AlertCircleIcon className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Could not load end of day data</p>
              <p className="mt-1">{error}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => load()}
            disabled={loading}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 shrink-0"
          >
            <RefreshCwIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Retry
          </button>
        </div>
      )}

      {loading && snapshot && (
        <div className="mb-4 flex items-center gap-2 text-sm text-gray-500">
          <Loader2Icon className="w-4 h-4 animate-spin" />
          Refreshing…
        </div>
      )}

      {!filtered && !loading && error && (
        <MobileEmptyState
          title="End of day data unavailable"
          description="Try again, pick a different date, or contact an admin if this keeps happening."
        />
      )}

      {filtered && (
        <>
          <div className="lg:hidden mb-4">
            <MobileStatStrip
              stats={[
                { key: 'sched', label: 'Scheduled', value: filtered.scheduledCount, tone: 'amber' },
                {
                  key: 'pct',
                  label: 'Completed %',
                  value: `${filtered.completionRate}%`,
                  tone: 'green',
                },
                { key: 'exc', label: 'Exceptions', value: filtered.exceptions.length, tone: 'amber' },
                canSeePricing
                  ? {
                      key: 'rev',
                      label: 'Actual rev.',
                      value: fmtMoney(filtered.totalActualRevenue),
                      tone: 'green',
                    }
                  : {
                      key: 'done',
                      label: 'Done',
                      value: filtered.completedToday.length,
                    },
              ]}
            />
          </div>

          <div className="hidden lg:grid grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
              <p className="text-sm text-gray-600">Scheduled</p>
              <p className="text-2xl font-bold">{filtered.scheduledCount}</p>
            </div>
            <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
              <p className="text-sm text-gray-600">Completion rate</p>
              <p className="text-2xl font-bold">{filtered.completionRate}%</p>
            </div>
            <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
              <p className="text-sm text-gray-600">Exceptions</p>
              <p className="text-2xl font-bold">{filtered.exceptions.length}</p>
            </div>
            {canSeePricing ? (
              <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
                <p className="text-sm text-gray-600">Revenue (actual)</p>
                <p className="text-2xl font-bold">{fmtMoney(filtered.totalActualRevenue)}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Quoted {fmtMoney(filtered.totalQuotedRevenue)}
                </p>
              </div>
            ) : (
              <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
                <p className="text-sm text-gray-600">Completed today</p>
                <p className="text-2xl font-bold">{filtered.completedToday.length}</p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <Link
              to="/messages?tab=external"
              className="bg-white p-4 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
            >
              <p className="text-sm font-medium text-gray-900">Inbox — staff unassigned</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                <WidgetCount loading={widgetsLoading} value={filtered.unassignedInboxCount} />
              </p>
            </Link>
            <Link
              to="/time-review"
              className="bg-white p-4 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
            >
              <p className="text-sm font-medium text-gray-900">Time — unapproved today</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                <WidgetCount loading={widgetsLoading} value={filtered.unapprovedTimeCount} />
              </p>
            </Link>
          </div>

          <div className="flex flex-wrap gap-3 mb-4">
            <input
              type="text"
              placeholder="Filter by crew name"
              value={crewFilter}
              onChange={(e) => setCrewFilter(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-2"
            />
            <select
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">All service types</option>
              {serviceTypes.map((st) => (
                <option key={st} value={st}>{st}</option>
              ))}
            </select>
          </div>

          {canAct && (
            <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200 flex flex-wrap items-center gap-3">
              <button
                type="button"
                disabled={signingOff || officeSigned}
                onClick={() => handleSignoff('office')}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 text-sm"
              >
                {officeSigned ? 'Office close recorded' : 'Office soft close (~5pm)'}
              </button>
              <button
                type="button"
                disabled={signingOff || finalSigned}
                onClick={() => handleSignoff('final')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm"
              >
                {finalSigned ? 'Final close recorded' : 'Final close (morning)'}
              </button>
              <p className="text-xs text-gray-500">{weeklyStatus.hint}</p>
            </div>
          )}

          <EndOfDaySection
            title="Exceptions"
            jobs={filtered.exceptions}
            empty="No exceptions — good close-out."
            canSeePricing={canSeePricing}
            canAct={canAct}
            onOpen={setPanelJob}
            onNavigate={(job) => navigate(jobDetailPath(job))}
          />
          <EndOfDaySection
            title="In progress (scheduled today)"
            jobs={filtered.inProgressToday}
            empty="No jobs still in progress for this date."
            canSeePricing={canSeePricing}
            canAct={canAct}
            onOpen={setPanelJob}
            onNavigate={(job) => navigate(jobDetailPath(job))}
          />
          <EndOfDaySection
            title="Other in progress"
            jobs={filtered.otherInProgress}
            empty="No other in-progress jobs."
            canSeePricing={canSeePricing}
            canAct={canAct}
            onOpen={setPanelJob}
            onNavigate={(job) => navigate(jobDetailPath(job))}
          />
          <EndOfDaySection
            title="Not started"
            jobs={filtered.notStarted}
            empty="Every scheduled job was started or completed."
            canSeePricing={canSeePricing}
            canAct={canAct}
            onOpen={setPanelJob}
            onNavigate={(job) => navigate(jobDetailPath(job))}
          />
          <EndOfDaySection
            title="Completed today"
            jobs={filtered.completedToday}
            empty="No jobs completed on this date yet."
            canSeePricing={canSeePricing}
            canAct={canAct}
            onOpen={setPanelJob}
            onNavigate={(job) => navigate(jobDetailPath(job))}
          />
        </>
      )}

      {panelJob && (
        <EndOfDayJobPanel
          job={panelJob}
          canAct={canAct}
          canSeePricing={canSeePricing}
          onClose={() => setPanelJob(null)}
          onUpdated={() => {
            load()
            setPanelJob(null)
          }}
        />
      )}
    </MobilePage>
  )
}
